import React, { useEffect, useState } from "react";

import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import {
  QUIZ_MAX_OPTIONS,
  QUIZ_MIN_OPTIONS,
  QUIZ_OPTION_KEYS,
  QUIZ_QUESTION_MAX,
  clampQuizFields,
  emptyQuizQuestion,
  validateQuizRow,
} from "./quizHelpers";

const MIN_CATEGORIES_FOR_QUESTIONS = 3;

/** Options are no longer typed by hand — they always mirror the theme's answer categories. */
function applyCategoriesToDraft(base, categories) {
  const names = (categories || [])
    .map((c) => String(c?.name ?? "").trim())
    .filter(Boolean)
    .slice(0, QUIZ_MAX_OPTIONS);

  const next = { ...base };
  QUIZ_OPTION_KEYS.forEach((key, i) => {
    next[key] = names[i] || "";
  });
  next.showOption = Math.max(
    QUIZ_MIN_OPTIONS,
    Math.min(QUIZ_MAX_OPTIONS, names.length || QUIZ_MIN_OPTIONS)
  );

  const currentAnswer = String(next.correct_answer ?? "").trim().toLowerCase();
  const stillValid = names.some((n) => n.toLowerCase() === currentAnswer);
  if (!stillValid) next.correct_answer = "";

  return next;
}

export default function QuizQuestionModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
  isSaving,
  savingLabel,
  categories,
}) {
  const [draft, setDraft] = useState(() => emptyQuizQuestion("temp"));
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) return;
    const base = initial
      ? { id: initial.id, ...clampQuizFields(initial) }
      : emptyQuizQuestion(`new-${Date.now()}`);
    setDraft(applyCategoriesToDraft(base, categories));
    setLocalError("");
  }, [open, mode, initial, categories]);

  if (!open) return null;

  const setField = (field, value, max = QUIZ_QUESTION_MAX) => {
    setDraft((prev) => ({ ...prev, [field]: value.slice(0, max) }));
    setLocalError("");
  };

  const finalize = async () => {
    setLocalError("");

    const check = validateQuizRow(draft, "", { requireQuestion: true });
    if (!check.ok) {
      setLocalError(check.message);
      return;
    }

    try {
      await onSubmit({ id: draft.id, ...check.data });
    } catch {
      /* parent surfaces error */
    }
  };

  const isEdit = mode === "edit";
  const categoryList = (categories || []).filter((c) => String(c?.name ?? "").trim());
  const hasEnoughCategories = categoryList.length >= MIN_CATEGORIES_FOR_QUESTIONS;

  return (
    <div className="quiz-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="quiz-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">{isEdit ? "Edit Question" : "New Question"}</h2>
          <button type="button" className="quiz-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="quiz-modal-body">
          {localError ? <p className="cr-alert cr-alert-error quiz-modal-error">{localError}</p> : null}

          {!hasEnoughCategories ? (
            <p className="cr-alert cr-alert-error">
              Add at least {MIN_CATEGORIES_FOR_QUESTIONS} answer categories in Rules &amp; Settings
              before adding questions.
            </p>
          ) : null}

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-question">Question</label>
            <textarea
              id="quiz-modal-question"
              className="quiz-modal-textarea"
              rows={4}
              value={draft.question_name}
              maxLength={QUIZ_QUESTION_MAX}
              onChange={(e) => setField("question_name", e.target.value, QUIZ_QUESTION_MAX)}
              placeholder="Enter the question"
              disabled={isSaving}
            />
            <p className="quiz-modal-limit">Max length {QUIZ_QUESTION_MAX} characters</p>
          </div>

          <div className="quiz-modal-field quiz-modal-field--full">
            <span className="quiz-modal-correct-label">Correct answer category</span>
            {hasEnoughCategories ? (
              <div className="quiz-modal-category-grid">
                {categoryList.map((cat) => {
                  const name = String(cat.name).trim();
                  const imageUrl = cat.image ? getDvSourceImageUrl(cat.image) : "";
                  return (
                    <label
                      key={name}
                      className={`quiz-modal-category-option${
                        draft.correct_answer === name ? " is-selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="quiz-correct-answer"
                        value={name}
                        checked={draft.correct_answer === name}
                        onChange={() => {
                          setDraft((prev) => ({ ...prev, correct_answer: name }));
                          setLocalError("");
                        }}
                        disabled={isSaving}
                      />
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt=""
                          className="quiz-modal-category-thumb"
                        />
                      ) : (
                        <span
                          className="quiz-modal-category-dot"
                          style={{ background: cat.color || "#999" }}
                          aria-hidden
                        />
                      )}
                      <span>{name}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="themeupdate-muted">
                No answer categories are configured for this theme yet.
              </p>
            )}
          </div>
        </div>

        <div className="quiz-modal-footer">
          <button
            type="button"
            className="quiz-modal-btn quiz-modal-btn-save"
            onClick={finalize}
            disabled={isSaving || !hasEnoughCategories}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden />
            {isSaving ? savingLabel || "Saving…" : isEdit ? "Update" : "Add New Question"}
          </button>
          <button type="button" className="quiz-modal-btn quiz-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
